import { Injectable } from "@angular/core";
import { EditingService, Measurement, } from "@evident/ngx-media-viewer";
import { MetadataService } from "@evident/ngx-metadata-writer";
import { NetworkStatusService } from "../../../../_services/network-status.service";
import { ElectronService } from "../../../../electron.service";
import { IPC_CHANNEL } from "../../../../_offline/electron.constants";
import { firstValueFrom } from "rxjs";
import { InspectionImage } from "../../../../_objects/inspection-image";
import { AnnotationObjectForUpdate } from "../../../../_objects/inspection-image-annotation";
import { AnnotationImageService } from "../../../../_services/http-api/annotation-image.service";
import { InspectionImageService } from "../../../../_services/http-api/inspection-image.service";
import { FileSystemAccessService } from "../../../../_services/file-system-access.service";
import { MerryMeasurementParserService } from "../../../../_services/merry-measurement-parser.service";

export const metadataMeasurementTag = "Measurements";
export const XMPMeasurementTag = "XMP-xmp:" + metadataMeasurementTag;

@Injectable({ providedIn: "root" })
export class MeasurementService {

  constructor(
    private _editingService: EditingService,
    private _metadataService: MetadataService,
    private _annotationService: AnnotationImageService,
    private networkService: NetworkStatusService,
    private electronService: ElectronService,
    private inspectionImageService: InspectionImageService,
    private fileSystemAccessService: FileSystemAccessService,
    private measurementParser: MerryMeasurementParserService
  ) {}

  public async retrieveExistingMeasurements(imageSrc: string): Promise<void> {
    await this.parseAndSetMeasurements(imageSrc);
  }

  public async saveMeasurements(imageSrc: string, inspectionImage: InspectionImage, base64ImagePreview: string) {
    const measurements = this._editingService.measurements;
    await this.writeMetadata(imageSrc, measurements);
    await this.saveMeasuredImage(inspectionImage, base64ImagePreview, imageSrc);
 }

  private async writeMetadata(imageSrc: string, measurements: Measurement[]) {
    const metadata: Record<string, string> = {};

    metadata[XMPMeasurementTag] = await this._metadataService.encryptMetadata(
      JSON.stringify(measurements),
    );
    await this._metadataService.embedMetadata(imageSrc, metadata);
  }

  private async parseAndSetMeasurements(imageSrc: string): Promise<void> {
    const measurements = await this.measurementParser.parseMeasurementResults(imageSrc);
    if (measurements.length)
      this._editingService.measurements = measurements;
  }

  private async saveMeasuredImage(inspectionImage: InspectionImage, base64ImagePreview: string, imageSrc: string) {
    // Really important to pass an empty array to the annotation for measurement, otherwise it won't consider the measured image
    const annotationReq = new AnnotationObjectForUpdate(inspectionImage.id, null, [], inspectionImage.schemaVersion);
    const measurementPreviewFile = await this.base64ToFile(base64ImagePreview, inspectionImage.fileName);

    await firstValueFrom(this._annotationService.putImageAnnotations(annotationReq, inspectionImage.inspectionId, 2));
    await firstValueFrom(this._annotationService.putModifiedImage(inspectionImage.inspectionId, inspectionImage.id, measurementPreviewFile, 2));

    if (this.networkService.isOnline) {
        const localPath = decodeURIComponent(imageSrc.replace('file:///', ''));
        const originalFileWithMetadata = await this.fileSystemAccessService.getFile(localPath, inspectionImage.fileName, inspectionImage.mimeType, true);
        await firstValueFrom(this.inspectionImageService.updateInspectionImageFile(inspectionImage.inspectionId, inspectionImage.id, originalFileWithMetadata));
    }
  }

  private async base64ToFile(base64: string, fileName: string) {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type });
  }

  public async getLocalImageUrl(imageSrc: string, inspectionImage: InspectionImage): Promise<string> {
    if (this.networkService.isOffline) {
      return imageSrc;
    } else {
      return await this.electronService.invoke(IPC_CHANNEL.FILE.SAVE_FILE, inspectionImage.originalFilePath, inspectionImage.fileName);
    }
  }
}
